/**
* @ngdoc Service
*/
import * as Sentry from "@sentry/browser";
import moment from 'moment';
import { merge } from "lodash";
(function () {
    'use strict';

    angular.module('vrm.core')
        .constant("vrmUserAccessLevel", {
            "DEMO": 0,
            "USER": 1,
            "ADMIN": 2,
            "RESTRICTED_ADMIN": 3,
            "DEALER": 4,
            "SITE_SHARE": 5,
            "AUTOMATION": 6,
            "SET_UP_MFA": 7
        })
        .service('UserService', UserService);

    /* @ngInject */
    function UserService($rootScope, localStorageService, ApiService, vrmUserAccessLevel, vrmNotificationsService, vrmNotificationType, locale, $q, WhitelabelService) {
        var service = {
            user: null,
            loading: _getUser(),
            getUser: getUser,
            reload: reload,
            save: save,
            setDealer: setDealer,
            setSettings: setSettings,
            deleteUserAccount: deleteUserAccount,
            getSiteCountFromLocalStorage,
            setSiteCountToLocalStorage,
        };

        $rootScope.vrmUserAccessLevel = vrmUserAccessLevel;

        $rootScope.$on('logout', function () {
            // Clear installations on logout
            service.user = null;
            WhitelabelService.setWhitelabel(false);
        });

        $rootScope.$on('login', function () {
            //Reload data on login
            service.loading = _getUser();
        });

        function weekStart(region, language) {
            const regionSat = 'AEAFBHDJDZEGIQIRJOKWLYOMQASDSY'.match(/../g);
            const regionSun = 'AGARASAUBDBRBSBTBWBZCACNCODMDOETGTGUHKHNIDILINJMJPKEKHKRLAMHMMMOMTMXMZNINPPAPEPHPKPRPTPYSASGSVTHTTTWUMUSVEVIWSYEZAZW'.match(/../g);
            const languageSat = ['ar', 'arq', 'arz', 'fa'];
            const languageSun = 'amasbndzengnguhehiidjajvkmknkolomhmlmrmtmyneomorpapssdsmsnsutatethtnurzhzu'.match(/../g);

            return (
                region ? (
                    regionSun.includes(region) || regionSat.includes(region) ? 0 : 1) : (
                    languageSun.includes(language) || languageSat.includes(language) ? 0 : 1));
        }

        function weekStartLocale(locale) {
            const parts = locale.match(/^([a-z]{2,3})(?:-([a-z]{3})(?=$|-))?(?:-([a-z]{4})(?=$|-))?(?:-([a-z]{2}|\d{3})(?=$|-))?/i);
            return weekStart(parts[4], parts[1]);
        }

        function _getUser() {
            if (ApiService.isShare()) {
                WhitelabelService.setWhitelabel(false);
                var reject = $q.defer();
                reject.reject(false);
                return reject.promise;
            }
            return ApiService.users('info', []).then(function (response) {
                if (window.Android && window.Android.consumeToken) {
                    window.Android.consumeToken(localStorageService.get("token"), ApiService.getUserId(), response.data.email);
                }
                if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.consumeToken) {
                    window.webkit.messageHandlers.consumeToken.postMessage({
                        token: localStorageService.get("token"),
                        userId: String(ApiService.getUserId()),
                        email: response.data.email
                    })
                }
                // Set whitelabel
                WhitelabelService.setWhitelabel(response.data.whiteLabel ? response.data.whiteLabel : false);
                service.user = response.data;
                document.documentElement.setAttribute('lang', response.data.language || 'en');
                let weekStart = 0;
                switch (response.data.weekday) {
                    case ("sunday"):
                        weekStart = 0;
                        break;
                    case ("monday"):
                        weekStart = 1;
                        break;
                    case ("auto"):
                        weekStart = weekStartLocale(navigator.language);
                        break;
                    default:
                        weekStart = weekStartLocale(navigator.language);
                        break;
                }
                moment.updateLocale(moment.locale(), { week: { dow: weekStart } });

                // Check mfa requirements
                const isAdmin = service.user.accessLevel === vrmUserAccessLevel.ADMIN ||
                    service.user.accessLevel === vrmUserAccessLevel.RESTRICTED_ADMIN;

                if (isAdmin && !service.user.twostepEnabled) {
                    console.warn('Disabling vrm access for user, two factor is required');
                    // Downgrade access level until 2fa is enabled
                    service.user.originalAccessLevel = service.user.accessLevel;
                    service.user.accessLevel = vrmUserAccessLevel.SET_UP_MFA;
                }

                $rootScope.$emit('user:loaded', service.user);

                return service.user;
            }, function () {
                WhitelabelService.setWhitelabel(false);
                return false;
            });
        }

        function getUser() {
            return service.loading;
        }

        function reload() {
            service.loading = _getUser();
            return service.loading;
        }

        function save(user) {
            return ApiService.users('update', {
                "name": user.name,
                "email": user.email,
                "sms_token": user.sms_token,
                "phone": user.phone,
                "company": user.company,
                "city": user.city,
                "country": user.country,
                "dealer": user.dealer_name,
                "screensaverTimeout": user.screensaverTimeout,
                "useFahrenheit": user.useFahrenheit,
                "useGallons": user.useGallons,
                "current_password": user.current_password,
                "password": user.password,
                "password_confirm": user.password_confirm,
                "weekday": user.weekday
            }).then(function (response) {
                service.user = merge(user, service.user);
                reload();
                if (response.data.success) {
                    vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString('core.settings_saved') });
                    if (response.data.emailVerificationExpiry) {
                        const expiresInHours = response.data.emailVerificationExpiry / 60 / 60;
                        vrmNotificationsService.add({ type: vrmNotificationType.INFO, message: locale.getString('profilesettings.email_validation_link_send', { hours: expiresInHours }) });
                    }
                }
                return response.data.success;
            }, function () {
                return false;
            });
        }

        function setDealer(dealer) {
            if (!dealer) { WhitelabelService.setWhitelabel(false); }
            return setSettings({
                "dealer_code": dealer
            });
        }

        function setSettings(settings) {
            return ApiService.users('settings', settings).then(function (response) {
                if (!response.data.success) {
                    vrmNotificationsService.add({ type: vrmNotificationType.ERROR, message: response.data.errors }, true);
                } else {
                    vrmNotificationsService.add({ type: vrmNotificationType.SUCCESS, message: locale.getString('core.settings_saved') });
                }
                return response.data.success;
            }, function () {
                return false;
            });
        }

        function deleteUserAccount(password) {
            return ApiService.deleteUserAccount({ password }).then(function (response) {
                vrmNotificationsService.add({
                    type: vrmNotificationType.SUCCESS,
                    message: locale.getString('profilesettings.delete_success')
                });
                return response.data.success;
            }).catch(() => false);
        }

        /**
         * Returns the local storage key for the site count for a specific user.
         *
         * @returns {null|string}
         */
        function getSiteCountStorageKey() {
            const userId = ApiService.getUserId();
            return userId && `${userId}.site_count`;
        }

        /**
         * Returns the stored site count from local storage for the signed-in user if any exists.
         *
         * If no site count is stored yet, returns null.
         *
         * @returns {*|null}
         */
        function getSiteCountFromLocalStorage() {
            const storageKey = getSiteCountStorageKey();

            if (storageKey) {
                return localStorageService.get(storageKey);
            }

            return null;
        }

        /**
         * Stores the site count for the signed-in user in local storage.
         *
         * This is needed on login to check where to redirect the user, as the login API endpoint does not return
         * a site count and the installations list is loaded after redirecting.
         *
         * @params {number} count - The site count to set in local storage.
         * @returns {*|null}
         */
        function setSiteCountToLocalStorage(count) {
            const storageKey = getSiteCountStorageKey();

            if (storageKey) {
                return localStorageService.set(storageKey, count);
            }
        }

        return service;
    }
})();
